/**
 * Web Push subscription helpers. Called after login (see auth-context).
 * The flow:
 *   1. Fetch the server's VAPID public key.
 *   2. Ask the browser for notification permission (respectfully — once).
 *   3. Register a PushSubscription and POST it to /api/push/subscribe.
 * All calls are best-effort — if the browser doesn't support push or the
 * user declines, we log and move on silently.
 */
import api from "@/lib/api";

const b64urlToUint8 = (b64url) => {
  const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  // Return the underlying ArrayBuffer — some Chromium builds validate the
  // buffer type strictly and reject typed-array views. Buffer is 100% safe.
  return arr.buffer;
};

const STORAGE_KEY = "push_permission_prompted";

export async function ensurePushSubscription({ prompt = true } = {}) {
  if (typeof window === "undefined") return { ok: false, reason: "ssr" };
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return { ok: false, reason: "unsupported" };
  }
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  if (!reg) return { ok: false, reason: "no-sw" };

  let permission = Notification.permission;
  if (permission === "default") {
    if (!prompt) return { ok: false, reason: "not-prompted" };
    // Only auto-prompt ONCE per browser — respect the user's earlier answer.
    if (localStorage.getItem(STORAGE_KEY) === "1") return { ok: false, reason: "already-declined" };
    localStorage.setItem(STORAGE_KEY, "1");
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") return { ok: false, reason: "denied" };

  // Get server VAPID public key
  let key;
  try {
    const { data } = await api.get("/push/vapid-public-key");
    key = data?.key;
  } catch (e) {
    return {
      ok: false,
      reason: "server-key-unavailable",
      detail: e?.response?.data?.detail || e?.message || "Server didn't return a VAPID key.",
    };
  }
  if (!key || typeof key !== "string" || key.length < 40) {
    return { ok: false, reason: "server-key-empty", detail: "Server returned an empty VAPID key." };
  }
  let appServerBuffer;
  try {
    appServerBuffer = b64urlToUint8(key); // ArrayBuffer
    const view = new Uint8Array(appServerBuffer);
    if (view.length !== 65 || view[0] !== 0x04) {
      return {
        ok: false,
        reason: "server-key-malformed",
        detail: `VAPID key decoded to ${view.length} bytes (expected 65 starting with 0x04).`,
      };
    }
  } catch (_e) {
    return { ok: false, reason: "server-key-decode-failed" };
  }

  // If there's an existing subscription against a DIFFERENT key, drop it first.
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    try {
      const existingKey = existing.options?.applicationServerKey;
      const currentView = new Uint8Array(appServerBuffer);
      const same = existingKey && new Uint8Array(existingKey).every((v, i) => v === currentView[i]);
      if (!same) {
        await existing.unsubscribe().catch(() => null);
      }
    } catch {
      // If we can't compare, be safe and re-subscribe from scratch.
      await existing.unsubscribe().catch(() => null);
    }
  }

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    // Try the ArrayBuffer form first (standard, widest support).
    try {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appServerBuffer });
    } catch (e1) {
      // Fallback: pass the base64url string form. Some Chromium builds normalize
      // internally and accept the string when they've rejected the buffer.
      try {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
      } catch (e2) {
        return {
          ok: false,
          reason: "subscribe-failed",
          detail: `${e1?.message || e1} · fallback: ${e2?.message || e2}`,
        };
      }
    }
  }
  await api.post("/push/subscribe", { subscription: sub.toJSON() });
  return { ok: true, subscription: sub };
}

export async function disablePush() {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    try { await api.post("/push/unsubscribe", { subscription: sub.toJSON() }); } catch (_) { /* ignore */ }
    await sub.unsubscribe().catch(() => null);
  }
}

/**
 * Nuclear reset — unsubscribes push, unregisters all service workers, and
 * wipes all Cache Storage. Used when the browser is stuck with a stale
 * PushSubscription bound to an old VAPID key ("applicationServerKey is not
 * valid" errors), or when the SW is serving a cached bundle after a deploy.
 * Caller should reload the page immediately after this resolves.
 */
export async function resetAppCache() {
  // 1. Kill any push subscription — remote + local.
  try {
    const reg = await navigator.serviceWorker.ready.catch(() => null);
    const sub = reg && (await reg.pushManager.getSubscription().catch(() => null));
    if (sub) {
      try { await api.post("/push/unsubscribe", { subscription: sub.toJSON() }); } catch (_) { /* ignore */ }
      await sub.unsubscribe().catch(() => null);
    }
  } catch (_) { /* ignore */ }
  // 2. Unregister every service worker for this origin.
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
    }
  } catch (_) { /* ignore */ }
  // 3. Wipe all Cache Storage buckets.
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
    }
  } catch (_) { /* ignore */ }
  // 4. Clear the local flag that stops us from re-prompting.
  try { localStorage.removeItem("push_permission_prompted"); } catch (_) { /* ignore */ }
}

